<template>
  <app-page title="Help Center" icon="info-circle" class="help-center-page">
    <template #custom-content>
      <div class="content-container">
        <div v-if="articlesQuery?.isLoading?.value" class="spinner">Loading...</div>
        <div v-else-if="articlesQuery?.error?.value" class="error">{{ articlesQuery?.error?.value }}</div>
        <div v-else>
          <div class="header-container">
            <div class="header-container--title">
              <font-awesome-icon v-if="!isHome" @click="onBack" class="icon" :icon="['fas', 'arrow-left']" />
              <h1>{{ article.title || 'Home' }}</h1>
            </div>
            <div>
              <div class="simple-search">
                <b-form-input
                  type="search"
                  placeholder="Search articles"
                  v-model="searchTerm"
                  @input="debouncedSearch"
                  @focus="showDropdown"
                  @blur="hideDropdown"
                  ref="searchInput"
                >
                </b-form-input>
                <div v-if="dropdownVisible" class="dropdown-wrapper">
                  <div class="fixed-child">
                    <a
                      v-for="(result, index) in searchResults"
                      :key="index"
                      @mousedown.prevent
                      @click="onClickArticle(result.slug)"
                      class="dropdown-item"
                    >
                      {{ result.title }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Home: categories shown as expandable groups so every article is reachable in place -->
          <div v-if="isHome">
            <div class="hc-toolbar">
              <p class="hc-lede">Browse every help topic below — expand a category to see its articles.</p>
              <button v-if="hasCategories" type="button" class="hc-expand-all" @click="toggleAll">
                <font-awesome-icon :icon="['fal', allOpen ? 'chevron-up' : 'chevron-down']" />
                {{ allOpen ? 'Collapse all' : 'Expand all' }}
              </button>
            </div>
            <div v-if="hasCategories" class="hc-accordion">
              <div
                v-for="category in article.related_articles"
                :key="category.slug"
                class="hc-acc-item"
                :class="{ 'hc-acc-open': isOpen(category.slug) }"
              >
                <!-- Category with no sub-articles: link straight to the page rather than expand to nothing -->
                <router-link
                  v-if="isLeaf(category.slug)"
                  :to="`/help-center/${category.slug}`"
                  class="hc-acc-head hc-acc-head--link"
                >
                  <span class="hc-cat-tile">
                    <font-awesome-icon :icon="CATEGORY_ICON" />
                  </span>
                  <span class="hc-acc-titles">
                    <span class="hc-acc-title">{{ category.title }}</span>
                    <span v-if="category.description" class="hc-acc-desc">{{ category.description }}</span>
                  </span>
                  <font-awesome-icon class="hc-acc-chev hc-acc-chev--nav" :icon="['fas', 'angle-right']" />
                </router-link>

                <!-- Category with sub-articles: expand in place -->
                <template v-else>
                  <button
                    type="button"
                    class="hc-acc-head"
                    :aria-expanded="isOpen(category.slug) ? 'true' : 'false'"
                    @click="toggleCategory(category.slug)"
                  >
                    <span class="hc-cat-tile">
                      <font-awesome-icon :icon="CATEGORY_ICON" />
                    </span>
                    <span class="hc-acc-titles">
                      <span class="hc-acc-title">{{ category.title }}</span>
                      <span v-if="category.description" class="hc-acc-desc">{{ category.description }}</span>
                    </span>
                    <span v-if="childArticles(category.slug).length" class="hc-acc-count">
                      {{ childArticles(category.slug).length }}
                      {{ pluralize('article', childArticles(category.slug).length) }}
                    </span>
                    <font-awesome-icon class="hc-acc-chev" :icon="['fal', 'chevron-down']" />
                  </button>
                  <div v-show="isOpen(category.slug)" class="hc-acc-body">
                    <router-link
                      v-for="child in childArticles(category.slug)"
                      :key="child.slug"
                      :to="`/help-center/${child.slug}`"
                      class="hc-article-row"
                    >
                      <font-awesome-icon class="hc-article-ic" :icon="['fas', 'file-lines']" />
                      <span class="hc-article-main">
                        <span class="hc-article-title">{{ child.title }}</span>
                      </span>
                      <font-awesome-icon class="hc-article-chev" :icon="['fas', 'angle-right']" />
                    </router-link>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Article / category page (also where saved category links land) -->
          <div v-else class="content-inner">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="article.contents" class="article-contents"></div>
            <div v-if="article.contents && article.type == 'article' && !hasRated" class="feedback-section">
              <p>Was this article helpful?</p>
              <div class="feedback-buttons">
                <b-button variant="light" @click="rateArticle('yes')" title="Yes">
                  <ThumbsUp :color="PaletteColors['brand-primary']" /> Yes
                </b-button>
                <b-button variant="light" @click="rateArticle('no')" title="No">
                  <ThumbsDown :color="PaletteColors['brand-primary']" /> No
                </b-button>
              </div>
            </div>
            <div v-if="article.related_articles && article.related_articles.length" class="hc-related">
              <h2 class="hc-related-title">Related Articles</h2>
              <div class="hc-list-card">
                <router-link
                  v-for="relatedArticle in article.related_articles"
                  :key="relatedArticle.slug"
                  :to="`/help-center/${relatedArticle.slug}`"
                  class="hc-article-row"
                >
                  <font-awesome-icon class="hc-article-ic" :icon="['fas', 'file-lines']" />
                  <span class="hc-article-main">
                    <span class="hc-article-title">{{ relatedArticle.title }}</span>
                    <span v-if="relatedArticle.description" class="hc-article-desc">
                      {{ relatedArticle.description }}
                    </span>
                  </span>
                  <font-awesome-icon class="hc-article-chev" :icon="['fas', 'angle-right']" />
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useRoute, useRouter } from 'vue-router/composables';
import { getArticle } from '@/queries/helpCenter';
import { pluralize } from '@/utils/common';
import AppPage from '@/components/AppPage.vue';
import ApiClient from '@/ApiClient';
import ThumbsUp from '@/assets/icons/ThumbsUp.vue';
import ThumbsDown from '@/assets/icons/ThumbsDown.vue';
import { PaletteColors } from '@/Theme';

const route = useRoute();
const router = useRouter();
const slug = ref(route.params.slug || 'home');
const isHome = ref(slug.value === 'home');

const searchTerm = ref('');
const dropdownVisible = ref(false);
const searchInput = ref(null);

let debounceTimeout;
const customDebounce = (func, delay) => {
  return (...args) => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }
    debounceTimeout = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const debouncedSearch = customDebounce(() => {
  onSearch(searchTerm.value);
}, 300);

const showDropdown = () => {
  dropdownVisible.value = true;
};

const hideDropdown = () => {
  dropdownVisible.value = false;
};

const articlesQuery = useQuery(getArticle(slug.value, false));
const searchResults = ref([]);

const article = ref(articlesQuery?.value || {});

const hasCategories = computed(() => !!article.value?.related_articles?.length);

// On the home page each "related article" is really a category that holds more
// articles. We fetch each category's children up front so we can show the article
// counts and let any/all groups expand in place — no second-level page required.
const categoryChildren = ref({});

const childArticles = categorySlug => categoryChildren.value[categorySlug] || [];

// A category becomes a "leaf" once we've loaded it and found no sub-articles (or
// the fetch failed): it links straight to its page instead of expanding to nothing.
const isLeaf = categorySlug =>
  categoryChildren.value[categorySlug] !== undefined && childArticles(categorySlug).length === 0;

const fetchCategoryChildren = async categories => {
  if (!categories?.length) {
    return;
  }
  await Promise.all(
    categories.map(async category => {
      if (categoryChildren.value[category.slug] !== undefined) {
        return;
      }
      try {
        const result = await ApiClient.renderArticle(category.slug);
        categoryChildren.value = {
          ...categoryChildren.value,
          [category.slug]: result?.data?.related_articles || [],
        };
      } catch (e) {
        // Treat a failed fetch as a leaf so the group links straight to its page.
        categoryChildren.value = {
          ...categoryChildren.value,
          [category.slug]: [],
        };
      }
    }),
  );
};

// Every category uses the same neutral icon so the list reads as one uniform group.
const CATEGORY_ICON = ['fad', 'folder-open'];

// Accordion open/closed state, keyed by category slug.
const openCategories = ref(new Set());
const isOpen = categorySlug => openCategories.value.has(categorySlug);
const toggleCategory = categorySlug => {
  const next = new Set(openCategories.value);
  if (next.has(categorySlug)) {
    next.delete(categorySlug);
  } else {
    next.add(categorySlug);
  }
  openCategories.value = next;
};
// "Expand all" only concerns categories that actually have articles to expand.
const expandableCategories = computed(() =>
  (article.value?.related_articles || []).filter(category => childArticles(category.slug).length),
);
const allOpen = computed(
  () => expandableCategories.value.length > 0 && expandableCategories.value.every(category => isOpen(category.slug)),
);
const toggleAll = () => {
  openCategories.value = allOpen.value ? new Set() : new Set(expandableCategories.value.map(category => category.slug));
};

const hasRated = ref(false);
const updateHasRated = () => {
  const ratedArticles = JSON.parse(window.localStorage.getItem('rated-articles')) || {};
  const threeDaysAgo = Date.now() - 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds

  for (const slug in ratedArticles) {
    if (ratedArticles[slug] < threeDaysAgo) {
      delete ratedArticles[slug];
    }
  }

  window.localStorage.setItem('rated-articles', JSON.stringify(ratedArticles));
  hasRated.value = !!ratedArticles[article.value.slug];
};

watch(article, () => {
  updateHasRated();
  if (isHome.value) {
    fetchCategoryChildren(article.value?.related_articles);
  }
});

const rateArticle = helpful => {
  ApiClient.postAnalytics('rate-article', {
    articleSlug: article.value.slug,
    helpful: helpful,
  });

  let ratedArticles = {};
  try {
    ratedArticles = JSON.parse(window.localStorage.getItem('rated-articles')) || {};
  } catch (e) {
    ApiClient.postAnalytics('rate-article-failed', {
      msg: 'Failed to parse rated-articles from localStorage',
      error: JSON.stringify(e),
    });
  }

  ratedArticles[article.value.slug] = Date.now();

  window.localStorage.setItem('rated-articles', JSON.stringify(ratedArticles));

  hasRated.value = true;
};

const onSearch = async () => {
  if (!searchTerm.value) {
    searchResults.value = [];
    return;
  }
  const result = await ApiClient.searchArticles(searchTerm.value, false);
  searchResults.value = result.data;
  showDropdown(true);
};

const onClear = () => {
  searchResults.value = [];
  searchTerm.value = '';
};

const onClickArticle = slug => {
  onClear();
  router.push(`/help-center/${slug}`);
};

const onBack = () => {
  router.go(-1);
};

onMounted(() => {
  slug.value = route.params.slug || 'home';
  isHome.value = slug.value === 'home';
});

watch(articlesQuery.fetchStatus, () => {
  if (articlesQuery?.data.value) {
    article.value = articlesQuery.data.value;
  }
});
</script>

<style scoped>
.content-inner {
  display: flex;
  flex-direction: column;
}

.feedback-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 300px;
  align-self: center;
  padding: 1rem;
  border-radius: 0.5rem;
  background: var(--palette-color-base-white);
}

.feedback-buttons {
  display: flex;
  gap: 1rem;
}

.content-container {
  width: 100%;
  padding: 0 1rem;
}

@media (min-width: 769px) {
  .content-container {
    padding: 0;
  }
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.header-container--title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-container h1 {
  margin: 0;
  margin-left: 16px;
  font-size: 24px;
}

@media (max-width: 576px) {
  .header-container {
    flex-wrap: wrap;
  }

  .header-container h1 {
    font-size: 20px;
  }
}

.spinner {
  text-align: center;
  padding: 20px;
}

.error {
  color: var(--palette-color-danger);
  text-align: center;
  padding: 20px;
}

.article-contents {
  margin-top: 20px;
}

/* ===== Home: expandable category groups ===== */
.hc-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.5rem 0 1rem;
}

.hc-lede {
  margin: 0;
  color: var(--palette-color-default-lighten-20);
  font-size: 15px;
}

.hc-expand-all {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  padding: 8px 13px;
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 9px;
  background: var(--palette-color-base-white);
  color: var(--palette-color-brand-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.hc-expand-all:hover {
  border-color: var(--palette-color-brand-primary);
}

.hc-accordion {
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 14px;
  overflow: hidden;
}

.hc-acc-item + .hc-acc-item {
  border-top: 1px solid var(--palette-color-default-lighten-90);
}

.hc-acc-head {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 20px;
  border: 0;
  background-color: transparent;
  color: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.12s ease;
}

/* Keyboard focus ring for the expandable header. */
.hc-acc-head:focus-visible {
  outline: 2px solid var(--palette-color-brand-primary);
  outline-offset: -2px;
}

.hc-acc-head:hover {
  background-color: var(--palette-color-default-lighten-95);
}

.hc-cat-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background: var(--palette-color-core-1-lighten-80);
  color: var(--palette-color-brand-primary);
  font-size: 16px;
  transition: background 0.12s ease, color 0.12s ease;
}

.hc-acc-titles {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.hc-acc-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--palette-color-default);
}

.hc-acc-desc {
  margin-top: 1px;
  color: var(--palette-color-default-lighten-20);
  font-size: 13px;
}

.hc-acc-count {
  flex: 0 0 auto;
  color: var(--palette-color-default-lighten-50);
  font-size: 13px;
  font-weight: 600;
}

.hc-acc-chev {
  flex: 0 0 auto;
  width: 16px;
  text-align: center;
  color: var(--palette-color-default-lighten-50);
  font-size: 14px;
  transition: transform 0.2s ease, color 0.12s ease;
}

.hc-acc-head:hover .hc-acc-chev {
  color: var(--palette-color-brand-primary);
}

.hc-acc-item.hc-acc-open .hc-acc-chev {
  transform: rotate(180deg);
  color: var(--palette-color-brand-primary);
}

/* The leaf "navigate" chevron points right and never rotates. */
.hc-acc-chev--nav {
  transform: none;
}

.hc-acc-item.hc-acc-open .hc-acc-title {
  color: var(--palette-color-brand-primary);
}

.hc-acc-item.hc-acc-open .hc-cat-tile {
  background: var(--palette-color-brand-primary);
  color: var(--palette-color-base-white);
}

.hc-acc-body {
  padding: 4px 12px 12px 64px;
  background: var(--palette-color-default-lighten-95);
  text-align: left;
}

/* ===== Article rows (shared: accordion + category page) ===== */
.hc-related {
  margin-top: 24px;
}

.hc-related-title {
  margin: 0 0 12px;
  font-size: 16px;
}

.hc-list-card {
  background: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-90);
  border-radius: 14px;
  overflow: hidden;
  text-align: left;
}

.hc-list-card .hc-article-row {
  padding: 14px 18px;
}

.hc-article-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  text-decoration: none;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.hc-article-row:last-child {
  border-bottom: 0;
}

.hc-article-row:hover {
  background: var(--palette-color-core-1-lighten-80);
}

.hc-article-ic {
  flex: 0 0 auto;
  color: var(--palette-color-brand-primary);
  font-size: 13px;
  opacity: 0.55;
}

.hc-article-main {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.hc-article-title {
  color: var(--palette-color-brand-primary);
  font-size: 14.5px;
  font-weight: 600;
}

.hc-article-desc {
  margin-top: 1px;
  color: var(--palette-color-default-lighten-20);
  font-size: 13px;
}

.hc-article-chev {
  flex: 0 0 auto;
  color: var(--palette-color-brand-primary);
  font-size: 13px;
  opacity: 0.6;
  transition: transform 0.12s ease;
}

.hc-article-row:hover .hc-article-ic,
.hc-article-row:hover .hc-article-chev {
  opacity: 1;
}

.hc-article-row:hover .hc-article-chev {
  transform: translateX(2px);
}

.dropdown-wrapper {
  position: absolute;
  right: 0;
  background-color: var(--palette-color-base-white);
  border: 1px solid var(--palette-color-default-lighten-90);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  z-index: 1000;
}

.dropdown-item {
  padding: 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--palette-color-default-lighten-80);
  text-wrap: wrap;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: var(--palette-color-default-lighten-90);
}

.form-group {
  margin-bottom: 0;
}
</style>

<style>
/* Styles for Article content */

.article-contents {
  blockquote {
    background-color: var(--palette-color-base-white);
    padding: 1rem 1rem 0.25rem 1rem;
    border-radius: 5px;
  }

  p:empty,
  li:empty {
    display: none;
  }

  ol,
  ul {
    padding-left: 30px;
    margin-bottom: 22px;

    li {
      margin-bottom: 14px;
      padding-left: 4px;
    }
  }
}
</style>
